import { ComponentType, MouseEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import classNames from 'classnames';
import moment from 'moment';

import { DAY_FIRST_CALENDAR_FORMAT } from '../../lib/constants/date-time.constants';
import lang from '../../lib/language';
import { isBeforeDate } from '../../lib/util/DateTimeHelpers';

import ClearIcon from '../../assets/img/close-circle.svg';
import CalendarIcon from '../../assets/img/date-range-icon.svg';
import DownArrow from '../../assets/img/icon_expan_down_ratios.svg';
import LegacyClickableDiv from '../../components/ClickableDiv/ClickableDiv';
import LegacyClickableImage from '../../components/ClickableImage/ClickableImage';
import { DateRangeState } from '../../util/types';
import Button from '../Button/Button';
import { ButtonTypesEnum } from '../Button/Button.types';
import {
  createDateRangeSyncSnapshot,
  DATE_RANGE_KEY,
  formatDateToMarketData,
  formatInputDateValue,
  getDateRangeByDropdownOption,
  isDateRangeSyncSnapshotChanged,
  resolveDateRangeDropdownOption,
  resolveDateRangeFromProps,
} from '../DesktopCalendar/dateRangeDropdownHelpers';
import {
  CalendarViewEnum,
  DateRangeDropdownOptionEnum,
  RangeSelectionFieldEnum,
} from '../DesktopCalendar/DateRangePickerWrapper.types';
import useIsMobile from '../hooks/useIsMobile';
import MobileDrawer from '../MobileDrawer/MobileDrawer';

import { CalendarHeaderProps, getMobileRenderDay, MobileCalendarHeader } from './MobileFromToDatePickerCalendarParts';

import styles from './MobileFromToDatePicker.module.scss';

const ClickableDiv = LegacyClickableDiv as unknown as ComponentType<any>;
const ClickableImage = LegacyClickableImage as unknown as ComponentType<any>;

type Props = {
  label: string;
  minDateState?: string;
  maxDateState?: string;
  minDateCalendar?: Date | string;
  maxDateCalendar?: Date | string;
  initialState: DateRangeState;
  onDateSelect?: (date: DateRangeState) => void;
  withDateRangeDropdown?: boolean;
};

const DATE_RANGE_DROPDOWN_OPTIONS = [
  DateRangeDropdownOptionEnum.Tomorrow,
  DateRangeDropdownOptionEnum.OneWeek,
  DateRangeDropdownOptionEnum.OneMonth,
  DateRangeDropdownOptionEnum.CustomDate,
];

const formatSelectedDateRange = (startDateValue?: string, endDateValue?: string): string | undefined => {
  const start = startDateValue ? moment(startDateValue) : null;
  const end = endDateValue ? moment(endDateValue) : null;

  if (!start || !end || !start.isValid() || !end.isValid()) {
    return undefined;
  }

  if (start.isSame(end, 'day')) {
    return start.format(DAY_FIRST_CALENDAR_FORMAT);
  }

  return `${start.format(DAY_FIRST_CALENDAR_FORMAT)} - ${end.format(DAY_FIRST_CALENDAR_FORMAT)}`;
};

const getDateRangeDropdownOptionLabel = (option: DateRangeDropdownOptionEnum): string => {
  if (option === DateRangeDropdownOptionEnum.Tomorrow) {
    return lang.commonDateRangeTomorrow();
  }

  if (option === DateRangeDropdownOptionEnum.OneWeek) {
    return lang.commonDateRangeOneWeek();
  }

  if (option === DateRangeDropdownOptionEnum.OneMonth) {
    return lang.commonDateRangeOneMonth();
  }

  return lang.commonDateRangeCustomDate();
};

const MobileFromToDatePicker = ({
  label,
  minDateState,
  maxDateState,
  minDateCalendar,
  maxDateCalendar,
  initialState,
  onDateSelect,
  withDateRangeDropdown = false,
}: Props): ReactElement => {
  const { isMobile } = useIsMobile();

  const dropdownBaseDate = minDateCalendar ?? initialState?.startDate ?? new Date();
  const initialStateStartKey = formatInputDateValue(initialState?.startDate);
  const initialStateEndKey = formatInputDateValue(initialState?.endDate);
  const dropdownBaseDateKey = formatInputDateValue(dropdownBaseDate);
  const { startDate: initialStartDate, endDate: initialEndDate } = resolveDateRangeFromProps({
    minDateState,
    maxDateState,
    initialState,
    withDateRangeDropdown,
    dropdownBaseDate,
  });

  const [dateRangeState, setDateRangeState] = useState<DateRangeState>({
    startDate: initialStartDate,
    endDate: initialEndDate,
    key: DATE_RANGE_KEY,
  });
  const [tempRange, setTempRange] = useState<DateRangeState>(dateRangeState);
  const [displayCalendar, setDisplayCalendar] = useState<boolean>(false);
  const [displayDropdown, setDisplayDropdown] = useState<boolean>(false);
  const [selectedDropdownOption, setSelectedDropdownOption] = useState<DateRangeDropdownOptionEnum>(
    () => (
      withDateRangeDropdown
        ? resolveDateRangeDropdownOption(initialStartDate, initialEndDate, dropdownBaseDate)
        : DateRangeDropdownOptionEnum.CustomDate
    ),
  );
  const [view, setView] = useState<CalendarViewEnum>(CalendarViewEnum.Day);
  // eslint-disable-next-line
  const [selectedRangeField, setSelectedRangeField] = useState<RangeSelectionFieldEnum>(RangeSelectionFieldEnum.StartDate);
  const [inputValueStartDate, setInputValueStartDate] = useState<string | undefined>(
    minDateState ?? formatInputDateValue(initialState?.startDate),
  );
  const [inputValueEndDate, setInputValueEndDate] = useState<string | undefined>(
    maxDateState ?? formatInputDateValue(initialState?.endDate),
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showClearButton, setShowClearButton] = useState<boolean>(false);
  const [calendarNavigationDate, setCalendarNavigationDate] = useState<moment.Moment | null>(
    initialStartDate ? moment(initialStartDate) : null,
  );
  const syncedExternalRangeRef = useRef(createDateRangeSyncSnapshot(initialStartDate, initialEndDate));
  const calendarContentRef = useRef<HTMLDivElement>(null);

  const minDateMoment = useMemo(() => (minDateCalendar ? moment(minDateCalendar) : undefined), [minDateCalendar]);
  const maxDateMoment = useMemo(() => (maxDateCalendar ? moment(maxDateCalendar) : undefined), [maxDateCalendar]);
  const renderDay = useMemo(() => getMobileRenderDay(tempRange), [tempRange]);

  const calendarValue = useMemo(() => {
    if (calendarNavigationDate) {
      return calendarNavigationDate;
    }

    if (tempRange.startDate) {
      return moment(tempRange.startDate);
    }

    if (tempRange.endDate) {
      return moment(tempRange.endDate);
    }

    return null;
  }, [calendarNavigationDate, tempRange.endDate, tempRange.startDate]);

  useEffect(() => {
    moment.updateLocale('en', {
      week: { dow: 1, doy: 4 },
    });
  }, []);

  useEffect(() => {
    if (displayCalendar || displayDropdown) {
      return;
    }

    const nextExternalRange = resolveDateRangeFromProps({
      minDateState,
      maxDateState,
      initialState,
      withDateRangeDropdown,
      dropdownBaseDate,
    });
    const nextExternalSnapshot = createDateRangeSyncSnapshot(nextExternalRange.startDate, nextExternalRange.endDate);
    const currentInternalSnapshot = createDateRangeSyncSnapshot(dateRangeState.startDate, dateRangeState.endDate);
    const didExternalRangeChange = isDateRangeSyncSnapshotChanged(syncedExternalRangeRef.current, nextExternalSnapshot);
    const shouldSyncRange = isDateRangeSyncSnapshotChanged(currentInternalSnapshot, nextExternalSnapshot);

    if (didExternalRangeChange && shouldSyncRange) {
      updateRange(nextExternalRange.startDate, nextExternalRange.endDate);
      setCalendarNavigationDate(nextExternalRange.startDate ? moment(nextExternalRange.startDate) : null);
    }

    if (withDateRangeDropdown && didExternalRangeChange) {
      const resolvedOption = resolveDateRangeDropdownOption(
        nextExternalRange.startDate,
        nextExternalRange.endDate,
        dropdownBaseDate,
      );
      if (selectedDropdownOption !== resolvedOption) {
        setSelectedDropdownOption(resolvedOption);
      }
    }

    syncedExternalRangeRef.current = nextExternalSnapshot;

    if (withDateRangeDropdown) {
      return;
    }

    if (didExternalRangeChange && !minDateState && !maxDateState && showClearButton) {
      setShowClearButton(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayCalendar,
    displayDropdown,
    dropdownBaseDateKey,
    initialStateEndKey,
    initialStateStartKey,
    maxDateState,
    minDateState,
    selectedDropdownOption,
    showClearButton,
    withDateRangeDropdown,
  ]);

  const syncInputValues = (startDate?: Date | string, endDate?: Date | string) => {
    setInputValueStartDate(formatInputDateValue(startDate));
    setInputValueEndDate(formatInputDateValue(endDate));
  };

  const updateRange = (startDate?: Date | string, endDate?: Date | string) => {
    const nextRange = { startDate, endDate, key: DATE_RANGE_KEY };
    setDateRangeState(nextRange);
    setTempRange(nextRange);
    syncInputValues(startDate, endDate);
  };

  const emitDateSelection = (startDate?: Date | string, endDate?: Date | string) => {
    onDateSelect?.({
      startDate: formatDateToMarketData(startDate),
      endDate: formatDateToMarketData(endDate),
    });
  };

  const applyDropdownOption = (option: DateRangeDropdownOptionEnum) => {
    const { startDate, endDate } = getDateRangeByDropdownOption(option, dropdownBaseDate);

    updateRange(startDate, endDate);
    setSelectedDropdownOption(option);
    setDisplayDropdown(false);
    setDisplayCalendar(false);
    setAnchorEl(null);
    resetCalendarState();
    emitDateSelection(startDate, endDate);
  };

  const resetCalendarState = () => {
    setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
    setView(CalendarViewEnum.Day);
  };

  const handleDaySelect = (value: moment.Moment | null) => {
    if (!value) {
      return;
    }

    const selectedDate = value.toDate();

    if (view !== CalendarViewEnum.Day) {
      setCalendarNavigationDate(moment(selectedDate));
      setView(view === CalendarViewEnum.Year ? CalendarViewEnum.Month : CalendarViewEnum.Day);
      return;
    }

    if (selectedRangeField === RangeSelectionFieldEnum.StartDate) {
      const nextEndDate = tempRange.endDate
        && isBeforeDate(tempRange.endDate, selectedDate) ? selectedDate : (tempRange.endDate ?? selectedDate);

      setTempRange({
        startDate: selectedDate,
        endDate: nextEndDate,
        key: DATE_RANGE_KEY,
      });
      setSelectedRangeField(RangeSelectionFieldEnum.EndDate);
      setCalendarNavigationDate(moment(nextEndDate ?? selectedDate));
      return;
    }

    const nextStartDate = tempRange.startDate
      && isBeforeDate(selectedDate, tempRange.startDate) ? selectedDate : (tempRange.startDate ?? selectedDate);

    setTempRange({
      startDate: nextStartDate,
      endDate: selectedDate,
      key: DATE_RANGE_KEY,
    });
    setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
    setCalendarNavigationDate(moment(selectedDate));
  };

  const handleViewChange = (event, inputView: 'month' | 'year') => {
    event.stopPropagation();
    setView((selectedView) => {
      if (selectedView === inputView) {
        return CalendarViewEnum.Day;
      }

      return inputView as CalendarViewEnum;
    });
  };

  const closePicker = () => {
    setDisplayCalendar(false);
    setDisplayDropdown(false);
    setAnchorEl(null);
    resetCalendarState();
  };

  const openCustomDatePicker = (anchorElement: HTMLElement) => {
    setTempRange(dateRangeState);
    setAnchorEl(anchorElement);
    setDisplayDropdown(false);
    setDisplayCalendar(true);
    resetCalendarState();

    if (dateRangeState.startDate) {
      setCalendarNavigationDate(moment(dateRangeState.startDate));
    }
  };

  const openDropdownPicker = (anchorElement: HTMLElement) => {
    setAnchorEl(anchorElement);
    setDisplayDropdown(true);
    setDisplayCalendar(false);
    resetCalendarState();
  };

  const openDateRangeCalendar = (event: MouseEvent<HTMLElement>) => {
    if (withDateRangeDropdown) {
      if (selectedDropdownOption === DateRangeDropdownOptionEnum.CustomDate) {
        openCustomDatePicker(event.currentTarget);
        return;
      }

      openDropdownPicker(event.currentTarget);
      return;
    }

    openCustomDatePicker(event.currentTarget);
  };

  const clearDates = () => {
    if (withDateRangeDropdown) {
      applyDropdownOption(DateRangeDropdownOptionEnum.OneMonth);
      return;
    }

    updateRange(initialState?.startDate, initialState?.endDate);
    setShowClearButton(false);
    resetCalendarState();
    emitDateSelection(initialState?.startDate, initialState?.endDate);
  };

  const handleApply = () => {
    const { startDate } = tempRange;
    const endDate = tempRange.endDate ?? tempRange.startDate;
    const inputValueFrom = formatDateToMarketData(startDate);
    const inputValueTo = formatDateToMarketData(endDate);

    updateRange(startDate, endDate);

    if (withDateRangeDropdown) {
      setSelectedDropdownOption(DateRangeDropdownOptionEnum.CustomDate);
      setDisplayDropdown(false);
    } else {
      setShowClearButton(true);
    }
    closePicker();
    emitDateSelection(inputValueFrom, inputValueTo);
  };

  const handleCancel = () => {
    setTempRange(dateRangeState);
    closePicker();
  };

  const handleDropdownOptionSelect = (option: DateRangeDropdownOptionEnum) => {
    if (option === DateRangeDropdownOptionEnum.CustomDate) {
      setSelectedDropdownOption(DateRangeDropdownOptionEnum.CustomDate);
      setTempRange(dateRangeState);
      setDisplayDropdown(false);
      setDisplayCalendar(true);
      resetCalendarState();
      if (dateRangeState.startDate) {
        setCalendarNavigationDate(moment(dateRangeState.startDate));
      }
      return;
    }

    applyDropdownOption(option);
  };

  const isDefaultDropdownOption = selectedDropdownOption === DateRangeDropdownOptionEnum.OneMonth;
  const shouldShowClearButton = withDateRangeDropdown ? !isDefaultDropdownOption : showClearButton;
  const shouldUseMobileDropdownDrawer = isMobile && withDateRangeDropdown;

  let isPopoverOpen = displayCalendar;
  if (withDateRangeDropdown && !shouldUseMobileDropdownDrawer) {
    isPopoverOpen = displayDropdown || displayCalendar;
  }

  const isPickerOpen = shouldUseMobileDropdownDrawer ? (displayDropdown || displayCalendar) : isPopoverOpen;

  useEffect(() => {
    if (!isMobile || !displayCalendar) {
      return undefined;
    }

    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    const preventBackgroundScroll = (event: WheelEvent | TouchEvent) => {
      const target = event.target as Node;
      const isCalendarEvent = Boolean(calendarContentRef.current?.contains(target));

      if (!isCalendarEvent) {
        event.preventDefault();
      }
    };

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false });
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener('wheel', preventBackgroundScroll);
      document.removeEventListener('touchmove', preventBackgroundScroll);
    };
  }, [displayCalendar, isMobile]);

  const displaySelectedRange = (): string => {
    if (withDateRangeDropdown) {
      if (selectedDropdownOption !== DateRangeDropdownOptionEnum.CustomDate) {
        return getDateRangeDropdownOptionLabel(selectedDropdownOption);
      }

      return formatSelectedDateRange(inputValueStartDate, inputValueEndDate)
        ?? getDateRangeDropdownOptionLabel(selectedDropdownOption);
    }

    if (!showClearButton) {
      return label;
    }

    return formatSelectedDateRange(inputValueStartDate, inputValueEndDate) ?? label;
  };

  const renderDropdownOptions = (isInMobileDrawer = false) => {
    return (
      <div className={styles.dropdownOptions}>
        {DATE_RANGE_DROPDOWN_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            className={classNames(styles.dropdownOption, {
              [styles.dropdownOptionInDrawer]: isInMobileDrawer,
              [styles.dropdownOptionActive]: selectedDropdownOption === option,
            })}
            onClick={() => handleDropdownOptionSelect(option)}
          >
            {getDateRangeDropdownOptionLabel(option)}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.inputWrapper}>
        <ClickableDiv className={styles.date} onClick={openDateRangeCalendar}>
          <ClickableImage src={CalendarIcon} alt="Calendar" />
          <div className={styles.dateText}>{displaySelectedRange()}</div>
          {!shouldShowClearButton && (
            <ClickableImage
              className={classNames(styles.iconArrow, { [styles.arrowTransform]: isPickerOpen })}
              src={DownArrow}
              alt="arrow"
            />
          )}
        </ClickableDiv>
        {shouldShowClearButton && <ClickableImage className={styles.clearIcon} src={ClearIcon} onClick={clearDates} />}
      </div>
      <Popover
        anchorReference={isMobile ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={isMobile ? { top: window.innerHeight / 2, left: window.innerWidth / 2 } : undefined}
        anchorEl={isMobile ? undefined : anchorEl}
        classes={{ paper: classNames(styles.paper, { [styles.paperMobile]: isMobile }) }}
        open={isPopoverOpen}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        transformOrigin={{ vertical: 'center', horizontal: 'center' }}
        onClose={closePicker}
      >
        {!shouldUseMobileDropdownDrawer && withDateRangeDropdown && displayDropdown ? (
          renderDropdownOptions()
        ) : (
          <div ref={calendarContentRef} id="dateRangeWrapper" className={styles.dateRange}>
            <div className={styles.selectedRange}>
              <button
                type="button"
                className={classNames(styles.selectedRangeDate, {
            [styles.activeSelectedRangeDate]: selectedRangeField === RangeSelectionFieldEnum.StartDate,
            [styles.inactiveSelectedRangeDate]: selectedRangeField !== RangeSelectionFieldEnum.StartDate,
          })}
                onClick={() => {
            setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
            if (tempRange.startDate) {
              setCalendarNavigationDate(moment(tempRange.startDate));
            }
          }}
        >
                {moment(tempRange.startDate).format('MMM DD, YYYY')}
              </button>
              -
              <button
                type="button"
                className={classNames(styles.selectedRangeDate, {
            [styles.activeSelectedRangeDate]: selectedRangeField === RangeSelectionFieldEnum.EndDate,
            [styles.inactiveSelectedRangeDate]: selectedRangeField !== RangeSelectionFieldEnum.EndDate,
          })}
                onClick={() => {
            setSelectedRangeField(RangeSelectionFieldEnum.EndDate);
            if (tempRange.endDate) {
              setCalendarNavigationDate(moment(tempRange.endDate));
            }
          }}
        >
                {moment(tempRange.endDate).format('MMM DD, YYYY')}
              </button>
            </div>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
              <DateCalendar
                value={calendarValue}
                onChange={handleDaySelect}
                minDate={minDateMoment}
                maxDate={maxDateMoment}
                views={['year', 'month', 'day']}
                className={styles.dateRangePicker}
                view={view}
                slots={{
            day: renderDay,
            calendarHeader:
              (props: CalendarHeaderProps) =>
                <MobileCalendarHeader props={props} onToggleView={handleViewChange} />,
          }}
                dayOfWeekFormatter={(day) => day.format('dd')}
                onViewChange={(nextView) => setView(nextView as CalendarViewEnum)}
                onYearChange={() => setView(CalendarViewEnum.Day)}
        />
            </LocalizationProvider>
            <div className={styles.dateRangeButtons}>
              <Button
                variant={ButtonTypesEnum.Secondary}
                className={styles.cancelBtn}
                onClick={handleCancel}
        >
                {lang.commonCancelButton()}
              </Button>
              <Button
                variant={ButtonTypesEnum.Primary}
                className={styles.applyBtn}
                onClick={handleApply}
        >
                {lang.commonApply()}
              </Button>
            </div>
          </div>
        )}
      </Popover>
      {shouldUseMobileDropdownDrawer && (
        <MobileDrawer
          isOpen={displayDropdown}
          title={lang.commonDateRangePeriod()}
          onClose={closePicker}
          usePortal
        >
          <div className={styles.mobileDropdownDrawer}>
            {renderDropdownOptions(true)}
          </div>
        </MobileDrawer>
      )}
    </div>
  );
};

export default MobileFromToDatePicker;
